import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  Badge,
  Button,
  LoadingState,
  ErrorState,
  PremiumBadge,
} from "@/components/design-system";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import {
  paymentsCatalog,
  createCheckout,
  mySubscription,
  cancelSubscription,
} from "@/lib/payments";

const IntervalLabel = { month: "Monthly", year: "Annual" };

const ACCENT_BY_KEY = {
  founder_monthly: { badge: "founder", tint: "border-[#0B1F3A]/10" },
  founder_yearly: { badge: "founder", tint: "border-2 !border-[#2D7FF9]" },
  community_premium: { badge: "premium", tint: "border-2 !border-[#20C7E8]" },
  community_premium_yearly: { badge: "premium", tint: "border-2 !border-[#20C7E8]" },
  community_standard: { badge: "community", tint: "border-[#0B1F3A]/10" },
};

function BetaTag({ testId }) {
  return (
    <div
      className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#7ED957]/20 border border-[#7ED957]/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0B1F3A]"
      data-testid={testId || "beta-tag"}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#7ED957] animate-pulse" />
      Beta · free
    </div>
  );
}

function PriceCard({ price, isCurrent, onSelect, busy, testId, beta }) {
  const accent = ACCENT_BY_KEY[price.lookup_key] || {};
  const isYearly = price.interval === "year";
  const isPremium = price.lookup_key.startsWith("community_premium");
  const isStandard = price.lookup_key === "community_standard";
  const trialDays = price.trial_days || 0;
  const purchasable = price.purchasable !== false && !isStandard;
  return (
    <Card data-testid={testId} className={`!p-6 relative ${accent.tint || ""}`}>
      {beta && <BetaTag testId={`${testId}-beta-tag`} />}
      {isYearly && (
        <div className="absolute -top-3 left-6 bg-[#7ED957] text-[#0B1F3A] font-bold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full">
          Save 15%
        </div>
      )}
      {isPremium && (
        <div className="absolute -top-3 left-6">
          <PremiumBadge />
        </div>
      )}
      {isStandard && (
        <div className="absolute -top-3 left-6 bg-[#0B1F3A] text-white font-bold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full">
          Free forever
        </div>
      )}
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">
        {isStandard ? "Standard" : IntervalLabel[price.interval] || price.interval}
      </p>
      <h3 className="mt-2 font-[Montserrat] font-extrabold text-xl text-[#0B1F3A]">
        {price.product_name}
      </h3>
      <div className="mt-3 flex items-baseline gap-2">
        {isStandard ? (
          <>
            <span className="font-[Montserrat] font-extrabold text-5xl text-[#0B1F3A]">$0</span>
            <span className="text-[#0B1F3A]/60 text-sm">/ forever</span>
          </>
        ) : (
          <>
            <span className="font-[Montserrat] font-extrabold text-5xl text-[#0B1F3A]">
              ${price.display_amount}
            </span>
            <span className="text-[#0B1F3A]/60 text-sm">/ {price.interval}</span>
          </>
        )}
      </div>
      {trialDays > 0 && !isStandard && (
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#2D7FF9]/10 px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-[#2D7FF9]" data-testid={`${testId}-trial-badge`}>
          {trialDays}-day free trial
        </div>
      )}
      <p className="text-sm text-[#0B1F3A]/75 mt-3">{price.description}</p>
      <ul className="mt-4 space-y-2 text-sm text-[#0B1F3A]/85" data-testid={`${testId}-perks`}>
        {(price.perks || []).map((perk) => (
          <li key={perk} className="flex gap-2">
            <span className={`mt-1 shrink-0 w-1.5 h-1.5 rounded-full ${isPremium ? "bg-[#20C7E8]" : isStandard ? "bg-[#7ED957]" : "bg-[#2D7FF9]"}`} />
            <span>{perk}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        {isStandard ? (
          <Badge variant="active" data-testid={`${testId}-included`}>Included on signup</Badge>
        ) : isCurrent ? (
          <Badge variant="active">Current plan</Badge>
        ) : purchasable ? (
          <Button
            variant="primary"
            className="w-full"
            onClick={() => onSelect(price.lookup_key)}
            disabled={busy}
            data-testid={`billing-select-${price.lookup_key}`}
          >
            {busy
              ? "Redirecting…"
              : isPremium
              ? "Upgrade to Premium"
              : trialDays > 0
              ? `Start ${trialDays}-day free trial`
              : `Choose ${IntervalLabel[price.interval] || ""}`}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", err: "" });
  const [catalog, setCatalog] = useState(null);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const roles = user?.roles || [];
  const eligibleRole = roles.includes("founder") || roles.includes("community");

  const load = async () => {
    try {
      const [cat, s] = await Promise.all([paymentsCatalog(), mySubscription()]);
      setCatalog(cat);
      setSub(s);
      setState({ status: "ready", err: "" });
    } catch (err) {
      setState({ status: "error", err: formatApiError(err) });
    }
  };

  useEffect(() => { load(); }, []);

  const applicablePlans = useMemo(() => {
    if (!catalog?.prices) return [];
    return catalog.prices.filter((p) => {
      const req = p.applies_to_roles || [];
      if (req.length === 0) return true;
      return req.some((r) => roles.includes(r));
    });
  }, [catalog, roles]);

  const founderPlans = applicablePlans.filter((p) => p.lookup_key.startsWith("founder_"));
  const communityPlans = applicablePlans.filter((p) => p.lookup_key.startsWith("community_premium"));
  const communityStandard = applicablePlans.find((p) => p.lookup_key === "community_standard");
  const betaActive = !!catalog?.beta?.active;
  const betaNotice = catalog?.beta?.notice;

  const onSelect = async (lookupKey) => {
    setBusy(true);
    try {
      const res = await createCheckout(lookupKey);
      window.location.assign(res.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const onCancel = async () => {
    if (!confirm("Cancel your subscription? You'll retain access until the end of the current period.")) return;
    setCanceling(true);
    try {
      await cancelSubscription();
      toast.success("Subscription cancelled.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCanceling(false);
    }
  };

  if (!eligibleRole) {
    return (
      <div data-testid="billing-not-eligible">
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Billing</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Add a role from Settings to see billing.
        </h1>
        <div className="mt-6"><Link to="/app/settings"><Button variant="secondary">Go to Settings</Button></Link></div>
      </div>
    );
  }

  if (state.status === "loading") return <LoadingState label="Loading pricing…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  const active = sub?.subscription && sub.subscription.status === "active";
  const canceled = sub?.subscription && sub.subscription.status === "canceled";
  const currentKey = active ? sub.subscription.lookup_key : null;
  const isPremiumCurrent = active && currentKey === "community_premium";

  return (
    <div data-testid="billing-page" className="space-y-8 max-w-5xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Billing</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Choose your plan
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Every plan runs in a fully-simulated Stripe sandbox until real payments are enabled.
          Cancel any time from this page.
        </p>
      </div>

      {betaActive && (
        <div
          className="rounded-xl border-2 border-[#7ED957] bg-[#7ED957]/15 p-4 flex items-start gap-3"
          data-testid="billing-beta-banner"
        >
          <span className="mt-1 shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#7ED957] text-[#0B1F3A] text-[10px] font-black">β</span>
          <div>
            <p className="text-sm font-semibold text-[#0B1F3A]">{betaNotice || "Beta program — all subscriptions are temporarily free during Beta testing."}</p>
            <p className="text-xs text-[#0B1F3A]/70 mt-0.5">Founders get a 7-day free trial. Community members always stay free on the Standard plan.</p>
          </div>
        </div>
      )}

      {catalog?.mode === "mock" && (
        <div className="rounded-xl border border-[#20C7E8]/50 bg-[#20C7E8]/10 p-4">
          <p className="text-sm text-[#0B1F3A]" data-testid="billing-mock-banner">
            <b>Mock payments mode.</b> No card captured, no money moves.
          </p>
        </div>
      )}

      {(active || canceled) && (
        <Card data-testid="billing-current">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant={active ? "active" : "admin"}>{active ? "Active" : "Cancelled"}</Badge>
                {isPremiumCurrent && <PremiumBadge />}
                <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">
                  {sub.display_label || sub.subscription.lookup_key}
                </span>
              </div>
              <p className="mt-2 text-sm text-[#0B1F3A]/75">
                {active ? "Renews" : "Ends"} on <b>{new Date(sub.subscription.current_period_end).toLocaleDateString()}</b>
              </p>
            </div>
            {active && (
              <Button variant="secondary" onClick={onCancel} disabled={canceling} data-testid="billing-cancel">
                {canceling ? "Cancelling…" : "Cancel subscription"}
              </Button>
            )}
          </div>
        </Card>
      )}

      {communityPlans.length > 0 && (
        <section data-testid="billing-community-section">
          <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A]">Community — Standard & Premium</h2>
          <p className="text-sm text-[#0B1F3A]/70 mt-1 max-w-2xl">
            Standard is free forever — read products, submit feedback, and build reputation at no
            cost. Upgrade to Premium for a badge, extra reputation, and priority placement.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
            {communityStandard && (
              <PriceCard
                key={communityStandard.lookup_key}
                price={communityStandard}
                isCurrent={!active}
                onSelect={onSelect}
                busy={busy}
                beta={betaActive}
                testId={`billing-plan-${communityStandard.lookup_key}`}
              />
            )}
            {communityPlans.map((price) => (
              <PriceCard
                key={price.lookup_key}
                price={price}
                isCurrent={currentKey === price.lookup_key}
                onSelect={onSelect}
                busy={busy}
                beta={betaActive}
                testId={`billing-plan-${price.lookup_key}`}
              />
            ))}
          </div>
        </section>
      )}

      {founderPlans.length > 0 && (
        <section data-testid="billing-founder-section">
          <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A]">Founder — launch plan</h2>
          <p className="text-sm text-[#0B1F3A]/70 mt-1 max-w-2xl">
            Every founder plan starts with a <b>7-day free trial</b>. Cancel any time before it ends
            and you won&apos;t be charged.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
            {founderPlans.map((price) => (
              <PriceCard
                key={price.lookup_key}
                price={price}
                isCurrent={currentKey === price.lookup_key}
                onSelect={onSelect}
                busy={busy}
                beta={betaActive}
                testId={`billing-plan-${price.lookup_key}`}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}